/**
 * ConfirmModal — cross-platform themed confirmation dialog.
 *
 * Replaces Alert.alert / window.confirm with a custom modal
 * that renders consistently on iOS, Android, and Web.
 */

import React, { useId, useMemo } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Modal,
  useWindowDimensions,
} from 'react-native';
import { useTheme, ThemeColors } from '../../utils/theme';
import { FONT_SIZE, SPACING, RADIUS, SHADOW, OPACITY } from '../../utils/design-tokens';
import { useBackHandler } from '../../navigation/useBackHandler';

export interface ConfirmAction {
  label: string;
  onPress: () => void;
  variant?: 'primary' | 'danger' | 'outline';
}

interface Props {
  visible: boolean;
  title: string;
  message?: string;
  actions: ConfirmAction[];
  cancelLabel: string;
  onCancel: () => void;
  dismissOnBackdrop?: boolean;
}

export default function ConfirmModal({
  visible,
  title,
  message,
  actions,
  cancelLabel,
  onCancel,
  dismissOnBackdrop = true,
}: Props) {
  const { colors } = useTheme();
  const s = useMemo(() => createStyles(colors), [colors]);
  const { width } = useWindowDimensions();
  const modalW = Math.min(width - 48, 340);
  const modalId = useId();

  useBackHandler({
    id: `confirm-modal-${modalId}`,
    label: title,
    priority: 950,
    enabled: visible,
    onBack: () => {
      onCancel();
      return true;
    },
  });

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onCancel}>
      <TouchableOpacity
        style={s.overlay}
        activeOpacity={1}
        onPress={dismissOnBackdrop ? onCancel : undefined}
      >
        <TouchableOpacity style={[s.card, { width: modalW }]} activeOpacity={1}>
          <Text style={s.title}>{title}</Text>
          {message ? <Text style={s.message}>{message}</Text> : null}

          <View style={s.actions}>
            {actions.map((action, idx) => {
              const bg =
                action.variant === 'danger'
                  ? colors.danger
                  : action.variant === 'primary'
                  ? colors.primary
                  : 'transparent';
              const textColor =
                action.variant === 'outline' ? colors.primary : '#fff';
              const borderStyle =
                action.variant === 'outline'
                  ? { borderWidth: 1.5, borderColor: colors.border }
                  : undefined;

              return (
                <TouchableOpacity
                  key={idx}
                  style={[s.actionBtn, { backgroundColor: bg }, borderStyle]}
                  onPress={action.onPress}
                  activeOpacity={0.7}
                >
                  <Text style={[s.actionText, { color: textColor }]}>
                    {action.label}
                  </Text>
                </TouchableOpacity>
              );
            })}

            <TouchableOpacity
              style={s.cancelBtn}
              onPress={onCancel}
              activeOpacity={0.7}
            >
              <Text style={s.cancelText}>{cancelLabel}</Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: colors.overlay,
    justifyContent: 'center',
    alignItems: 'center',
  },
  card: {
    backgroundColor: colors.card,
    borderRadius: RADIUS.xl,
    paddingTop: SPACING.xl,
    paddingHorizontal: SPACING.lg,
    paddingBottom: SPACING.base,
    ...SHADOW.elevated,
  },
  title: {
    fontSize: FONT_SIZE.md,
    fontWeight: '700',
    color: colors.text,
    textAlign: 'center',
    marginBottom: SPACING.sm,
  },
  message: {
    fontSize: FONT_SIZE.sm,
    color: colors.textSecondary,
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: SPACING.lg,
  },
  actions: {
    gap: SPACING.md,
  },
  actionBtn: {
    borderRadius: RADIUS.md,
    paddingVertical: SPACING.md,
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionText: {
    fontSize: FONT_SIZE.base,
    fontWeight: '600',
  },
  cancelBtn: {
    paddingVertical: SPACING.md,
    alignItems: 'center',
  },
  cancelText: {
    fontSize: FONT_SIZE.sm,
    fontWeight: '500',
    color: colors.textSecondary,
  },
});
